import { EventNameToType } from '@common/events';
import {
  BaseSystem,
  EventSystem,
  FilteredComponentSystem,
  FilteredComponentSystemUpdateEntityArgs,
  PlayerUpdateSystem,
} from '@common/systems/SystemBase';

import { ClientWorld } from '../ClientWorld';
import { ClientRegistry } from '../components';
import { ClientServiceContainer } from '../services/ClientServiceContainer';

/**
 * Base class for client-side global systems (no component filtering).
 * Receives ClientServiceContainer which composes CommonServiceContainer.
 */
export abstract class ClientGlobalSystem extends BaseSystem<ClientWorld, ClientRegistry> {
  protected clientServices: ClientServiceContainer;

  constructor(clientServices: ClientServiceContainer) {
    super(clientServices.common);
    this.clientServices = clientServices;
  }

  protected override getWorld(): ClientWorld {
    return this.clientServices.clientWorld.getWorld();
  }
}

/**
 * Base class for client-side filtered component systems.
 * Receives ClientServiceContainer which composes CommonServiceContainer.
 */
export abstract class ClientFilteredComponentSystem extends FilteredComponentSystem<
  ClientWorld,
  ClientRegistry
> {
  protected clientServices: ClientServiceContainer;

  constructor(clientServices: ClientServiceContainer) {
    super(clientServices.common);
    this.clientServices = clientServices;
  }

  protected override getWorld(): ClientWorld {
    return this.clientServices.clientWorld.getWorld();
  }

  protected override getLocalPlayerEntityId(): number | undefined {
    return this.clientServices.localPlayer.entityId;
  }
}

/**
 * Base class for client-side event systems.
 * Receives ClientServiceContainer which composes CommonServiceContainer.
 */
export abstract class ClientEventSystem<
  TEvent extends keyof EventNameToType = keyof EventNameToType,
> extends EventSystem<ClientWorld, ClientRegistry, TEvent> {
  protected clientServices: ClientServiceContainer;

  constructor(clientServices: ClientServiceContainer) {
    super(clientServices.common);
    this.clientServices = clientServices;
  }

  protected override getWorld(): ClientWorld {
    return this.clientServices.clientWorld.getWorld();
  }
}

export interface FilteredClientComponentSystemUpdateEntityArgs extends FilteredComponentSystemUpdateEntityArgs<
  ClientRegistry,
  ClientWorld
> {}

/**
 * Base class for client-side systems that process the local player.
 * Uses LocalPlayerService to get the local player's entity ID.
 */
export abstract class ClientPlayerUpdateSystem extends PlayerUpdateSystem<
  ClientWorld,
  ClientRegistry
> {
  protected clientServices: ClientServiceContainer;

  constructor(clientServices: ClientServiceContainer) {
    super(clientServices.common);
    this.clientServices = clientServices;
  }

  protected override getWorld(): ClientWorld {
    return this.clientServices.clientWorld.getWorld();
  }

  protected override getPlayerIds(): number[] {
    const playerId = this.clientServices.localPlayer.playerId;
    return playerId !== undefined ? [playerId] : [];
  }

  protected override getEntityIdForPlayer(playerId: number): number | undefined {
    const localPlayer = this.clientServices.localPlayer;
    if (localPlayer.playerId === playerId) {
      return localPlayer.entityId;
    }
    return undefined;
  }
}
